import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Database, Car } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

export default function AdminModels() {
  const { user: me } = useAuth();
  const canEdit = ["platform_owner", "master_data_admin"].includes(me?.admin_role);
  const [params, setParams] = useSearchParams();
  const initialMake = params.get("make") || "";
  const [makes, setMakes] = useState([]);
  const [makeF, setMakeF] = useState(initialMake);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ make_id: initialMake, name: "", active: true });

  useEffect(() => {
    api.get("/admin/master-data/makes").then((r) => setMakes(r.data || []));
  }, []);

  const load = () => {
    setLoading(true);
    const p = {};
    if (makeF) p.make_id = makeF;
    if (q) p.q = q;
    api.get("/admin/master-data/models", { params: p })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [makeF]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const makeName = useMemo(() => makes.find((m) => m.id === makeF)?.name || "", [makes, makeF]);

  const openNew = () => {
    setEditing(null);
    setForm({ make_id: makeF || (makes[0]?.id ?? ""), name: "", active: true });
    setOpen(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ make_id: m.make_id, name: m.name, active: !!m.active });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim() || !form.make_id) { toast.error("Make and name required"); return; }
    try {
      if (editing) {
        await api.patch(`/admin/master-data/models/${editing.id}`, form);
        toast.success("Model updated");
      } else {
        await api.post("/admin/master-data/models", form);
        toast.success("Model created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.make_name} ${m.name}?`)) return;
    try {
      await api.delete(`/admin/master-data/models/${m.id}`);
      toast.success("Model deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-models-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Master Data</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <Database size={22} className="text-[#457b9d]" /> Vehicle Models
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            {items.length} model(s){makeName ? ` for ${makeName}` : " across all makes"}.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={openNew}
            data-testid="admin-models-new"
            className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
          >
            <Plus size={14} className="mr-1.5" /> New model
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={makeF}
          onChange={(e) => { setMakeF(e.target.value); setParams(e.target.value ? { make: e.target.value } : {}); }}
          data-testid="admin-models-make-filter"
          className="h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557] min-w-[180px]"
        >
          <option value="">All makes</option>
          {makes.map((mk) => <option key={mk.id} value={mk.id}>{mk.name}</option>)}
        </select>
        <Input
          data-testid="admin-models-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search models…"
          className="flex-1 min-w-[220px] max-w-md h-9 rounded-md text-[13px]"
        />
      </div>

      <div className="bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-models-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Model</th>
              <th className="text-left px-4 py-3 font-semibold">Make</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              {canEdit && <th className="text-right px-4 py-3 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {!loading && items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-[13px] text-[#667085]">No models yet.</td></tr>
            )}
            {items.map((m) => (
              <tr key={m.id} data-testid={`admin-model-row-${m.id}`} className="hover:bg-[#F7F9FC]">
                <td className="px-4 py-3 text-[13px] font-semibold text-[#1d3557] flex items-center gap-2">
                  <Car size={13} className="text-[#457b9d]" /> {m.name}
                </td>
                <td className="px-4 py-3 text-[12px] text-[#667085]">{m.make_name}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    m.active ? "bg-[#12B76A]/10 text-[#12B76A]" : "bg-[#66708515] text-[#667085]"
                  }`}>
                    {m.active ? "Active" : "Hidden"}
                  </span>
                </td>
                {canEdit && (
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-3">
                      <button
                        onClick={() => openEdit(m)}
                        data-testid={`admin-model-edit-${m.id}`}
                        className="text-[12px] text-[#457b9d] hover:underline font-semibold inline-flex items-center gap-1"
                      >
                        <Pencil size={11} /> Edit
                      </button>
                      <button
                        onClick={() => remove(m)}
                        data-testid={`admin-model-delete-${m.id}`}
                        className="text-[12px] text-[#e63946] hover:underline font-semibold inline-flex items-center gap-1"
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg border-[#E4E7EC] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1d3557] font-semibold">
              {editing ? "Edit model" : "New vehicle model"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Make</Label>
              <select
                data-testid="admin-model-make-select"
                value={form.make_id}
                onChange={(e) => setForm((f) => ({ ...f, make_id: e.target.value }))}
                className="mt-1 w-full h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
              >
                <option value="">Select make…</option>
                {makes.map((mk) => <option key={mk.id} value={mk.id}>{mk.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Model name</Label>
              <Input
                data-testid="admin-model-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 rounded-md"
                placeholder="e.g. Kushaq"
              />
            </div>
            <label className="flex items-center gap-2 text-[13px] text-[#1d3557] cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                data-testid="admin-model-active"
                className="accent-[#e63946]"
              />
              Visible to garages
            </label>
            <Button
              onClick={save}
              data-testid="admin-model-save"
              className="w-full h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
            >
              {editing ? "Save changes" : "Create model"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
